import { useCallback, useEffect, useMemo, useState } from 'react';
import type { InspectionOrderDevice, SaveDeviceInspectionResultsRequest } from '../../types/inspectionOrder';
import type { DeviceReportTemplate } from '../../types/reportTemplate';
import { Tabs } from '../ui/Tabs';
import { NfcPairAction } from '../nfc/NfcPairAction';
import api from '../../services/api';
import './OrderDeviceDetailsView.css';

interface OrderDeviceDetailsViewProps {
  device: InspectionOrderDevice;
  orderId: string;
  onBack: () => void;
  template: DeviceReportTemplate | null;
  isTemplateLoading: boolean;
  templateError: string | null;
  onReloadTemplate?: () => void;
  onDeviceUpdated?: (device: InspectionOrderDevice) => void;
}

type DeviceDetailsTab = 'inspection' | 'details';

type SaveState = {
  isSaving: boolean;
  isCompleting: boolean;
  successMessage: string | null;
  errorMessage: string | null;
};

const valueOrPlaceholder = (value: string | null | undefined, fallback = 'Keine Angabe'): string => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.length > 0) {
      return trimmed;
    }
  }
  return fallback;
};

const combineValueWithUnit = (value: string | null | undefined, unit?: string | null): string => {
  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    if (trimmedValue.length > 0) {
      const normalizedUnit = typeof unit === 'string' && unit.trim().length > 0 ? unit.trim() : null;
      return normalizedUnit ? `${trimmedValue} ${normalizedUnit}` : trimmedValue;
    }
  }
  return 'Keine Angabe';
};

export const OrderDeviceDetailsView = ({
  device,
  orderId,
  onBack,
  template,
  isTemplateLoading,
  templateError,
  onReloadTemplate,
  onDeviceUpdated
}: OrderDeviceDetailsViewProps) => {
  const [activeTab, setActiveTab] = useState<DeviceDetailsTab>('inspection');
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [saveState, setSaveState] = useState<SaveState>({
    isSaving: false,
    isCompleting: false,
    successMessage: null,
    errorMessage: null
  });
  const [currentNfcTagId, setCurrentNfcTagId] = useState<string | null>(device.nfcTagId ?? null);

  const tabs = useMemo(
    () => [
      { id: 'inspection', label: 'Prüfdaten' },
      { id: 'details', label: 'Gerätedetails' }
    ],
    []
  );

  const identifierLabel = useMemo(
    () => valueOrPlaceholder(device.identifier, 'Ohne Kennung'),
    [device.identifier]
  );

  const equipmentTypeLabel = useMemo(
    () => valueOrPlaceholder(device.equipmentType, 'Gerätetyp unbekannt'),
    [device.equipmentType]
  );

  const metadataEntries = useMemo(() => device.metadata ?? [], [device.metadata]);
  const fieldEntries = useMemo(() => device.fields ?? [], [device.fields]);
  const templateFields = useMemo(() => template?.fields ?? [], [template]);
  const templateNameLabel = useMemo(() => template?.templateName ?? null, [template]);

  const handleNfcPairingSuccess = useCallback((nfcTagId: string) => {
    setCurrentNfcTagId(nfcTagId);
  }, []);

  useEffect(() => {
    setCurrentNfcTagId(device.nfcTagId ?? null);
  }, [device.id, device.nfcTagId]);

  useEffect(() => {
    if (!template || !template.fields) {
      setFieldValues({});
      return;
    }

    const nextValues: Record<string, string> = {};
    
    // First, apply saved results from device if available
    if (device.inspectionResults) {
      Object.entries(device.inspectionResults).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          nextValues[key] = String(value);
        }
      });
    }
    
    // Then, apply default values for fields not already set
    template.fields.forEach((field) => {
      const key = field.id ?? field.name;
      if (!key) {
        return;
      }
      if (nextValues[key] === undefined || nextValues[key] === '') {
        if (field.defaultValue !== undefined && field.defaultValue !== null && String(field.defaultValue).length > 0) {
          nextValues[key] = String(field.defaultValue);
        }
      }
    });
    setFieldValues(nextValues);
  }, [template, device.inspectionResults]);

  const requiredFieldIds = useMemo(() => {
    if (!template || !template.fields) {
      return new Set<string>();
    }
    return new Set(
      template.fields
        .filter((field) => field.mandatory)
        .map((field) => field.id ?? field.name)
        .filter((key): key is string => key !== null && key !== undefined)
    );
  }, [template]);

  const allRequiredFieldsFilled = useMemo(() => {
    if (requiredFieldIds.size === 0) {
      return true;
    }
    return Array.from(requiredFieldIds).every((fieldId) => {
      const value = fieldValues[fieldId];
      return value !== undefined && value !== null && String(value).trim().length > 0;
    });
  }, [requiredFieldIds, fieldValues]);

  const handleTabSelect = useCallback((tabId: string) => {
    if (tabId === 'inspection' || tabId === 'details') {
      setActiveTab(tabId);
    }
  }, []);

  const handleFieldChange = useCallback((fieldKey: string, value: string) => {
    setFieldValues((previous) => ({
      ...previous,
      [fieldKey]: value
    }));
    setSaveState((prev) => ({ ...prev, successMessage: null, errorMessage: null }));
  }, []);

  const handleSave = useCallback(async () => {
    if (!device.id || saveState.isSaving || saveState.isCompleting) {
      return;
    }

    setSaveState({ isSaving: true, isCompleting: false, successMessage: null, errorMessage: null });

    try {
      const request: SaveDeviceInspectionResultsRequest = { results: fieldValues };
      const response = await api.put<InspectionOrderDevice>(
        `technicians/me/inspection-orders/${orderId}/devices/${device.id}/results`,
        request
      );
      const updatedDevice = (response as any)?.data ?? response;
      setSaveState({ isSaving: false, isCompleting: false, successMessage: 'Prüfergebnisse gespeichert', errorMessage: null });
      if (onDeviceUpdated && updatedDevice) {
        onDeviceUpdated(updatedDevice as InspectionOrderDevice);
      }
    } catch (error) {
      console.error('Failed to save device results', error);
      setSaveState({ isSaving: false, isCompleting: false, successMessage: null, errorMessage: 'Speichern fehlgeschlagen. Bitte Verbindung prüfen.' });
    }
  }, [device.id, fieldValues, orderId, onDeviceUpdated, saveState.isSaving, saveState.isCompleting]);

  const handleComplete = useCallback(async () => {
    if (!device.id || saveState.isSaving || saveState.isCompleting) {
      return;
    }

    setSaveState({ isSaving: false, isCompleting: true, successMessage: null, errorMessage: null });

    try {
      const request: SaveDeviceInspectionResultsRequest = { results: fieldValues };
      const response = await api.put<InspectionOrderDevice>(
        `technicians/me/inspection-orders/${orderId}/devices/${device.id}/complete`,
        request
      );
      const updatedDevice = (response as any)?.data ?? response;
      setSaveState({ isSaving: false, isCompleting: false, successMessage: 'Prüfung abgeschlossen', errorMessage: null });
      if (onDeviceUpdated && updatedDevice) {
        onDeviceUpdated(updatedDevice as InspectionOrderDevice);
      }
      onBack();
    } catch (error) {
      console.error('Failed to complete device inspection', error);
      setSaveState({ isSaving: false, isCompleting: false, successMessage: null, errorMessage: 'Speichern fehlgeschlagen. Bitte Verbindung prüfen.' });
    }
  }, [device.id, fieldValues, orderId, onBack, onDeviceUpdated, saveState.isSaving, saveState.isCompleting]);

  const statusLabel = device.statusLabel ?? (device.status === 'COMPLETED' ? 'Abgeschlossen' : 'Offen');

  return (
    <section className="order-device-details">
      <header className="order-device-details__header">
        <button type="button" className="order-device-details__back" onClick={onBack}>
          Zurück
        </button>
        <div className="order-device-details__summary">
          <div className="order-device-details__summary-main">
            <h3>{identifierLabel}</h3>
            <span
              className={`order-device-details__status-badge order-device-details__status-badge--${device.status?.toLowerCase() ?? 'pending'}`}
            >
              {statusLabel}
            </span>
          </div>
          <p className="order-device-details__subtitle">
            Gerätetyp: <span>{equipmentTypeLabel}</span>
          </p>
        </div>
      </header>

      <Tabs
        tabs={tabs}
        activeTabId={activeTab}
        onTabSelect={handleTabSelect}
        tablistLabel="Gerätedetails"
      />

      <div
        role="tabpanel"
        id="tabpanel-inspection"
        aria-labelledby="tab-inspection"
        className="order-device-details__tabpanel"
        hidden={activeTab !== 'inspection'}
      >
        <div className="order-device-details__panel">
          <section className="order-device-details__section">
            <div className="order-device-details__section-header">
              <h4 className="order-device-details__section-title">Prüfdaten</h4>
            </div>

            {isTemplateLoading && (
              <div className="order-device-details__alert order-device-details__alert--muted" role="status">
                <span className="order-device-details__spinner" aria-hidden="true" />
                <span>Prüfberichtsvorlage wird geladen ...</span>
              </div>
            )}

            {!isTemplateLoading && templateError && (
              <div className="order-device-details__alert order-device-details__alert--error" role="alert">
                <div>{templateError}</div>
                {onReloadTemplate && (
                  <button
                    type="button"
                    className="order-device-details__retry"
                    onClick={onReloadTemplate}
                  >
                    Erneut laden
                  </button>
                )}
              </div>
            )}

            {!isTemplateLoading && !templateError && (!template || templateFields.length === 0) && (
              <div className="order-device-details__empty">
                <span>Keine Prüfberichtsvorlage für diesen Gerätetyp vorhanden</span>
              </div>
            )}

            {!isTemplateLoading && !templateError && template && templateFields.length > 0 && (
              <div className="order-device-details__form">
                {templateNameLabel && (
                  <p className="order-device-details__template-name">
                    Vorlage: <strong>{templateNameLabel}</strong>
                  </p>
                )}
                <form className="order-device-details__field-grid" onSubmit={(event) => event.preventDefault()}>
                  {templateFields.map((field, index) => {
                    const fieldKey = field.id ?? field.name ?? String(index);
                    const currentValue =
                      fieldValues[fieldKey] ?? field.defaultValue ?? '';
                    const normalizedValue =
                      currentValue === null || currentValue === undefined ? '' : String(currentValue);
                    const options = (field.options ?? []).length > 0
                      ? field.options
                      : field.dataType === 'boolean'
                        ? [
                            { id: 'true', value: 'true', description: 'Ja' },
                            { id: 'false', value: 'false', description: 'Nein' }
                          ]
                        : [];
                    const isSelect = field.inputType === 'predefined' || options.length > 0;
                    const labelId = `report-field-${fieldKey}-${index}`;
                    const requiredBadge = field.mandatory ? (
                      <span className="order-device-details__required"> *</span>
                    ) : null;

                    const handleValueChange = (value: string) => {
                      handleFieldChange(fieldKey, value);
                    };

                    let control = (
                      <textarea
                        id={labelId}
                        value={normalizedValue}
                        onChange={(event) => handleValueChange(event.target.value)}
                        rows={field.dataType === 'text' ? 3 : 2}
                        placeholder="Wert eingeben"
                      />
                    );

                    if (isSelect) {
                      control = (
                        <select
                          id={labelId}
                          value={normalizedValue}
                          onChange={(event) => handleValueChange(event.target.value)}
                        >
                          <option value="">Bitte auswählen</option>
                          {options.map((option) => (
                            <option key={option.id ?? option.value} value={option.value}>
                              {option.description ? option.description : option.value}
                            </option>
                          ))}
                        </select>
                      );
                    } else if (field.dataType === 'number') {
                      control = (
                        <input
                          id={labelId}
                          type="number"
                          value={normalizedValue}
                          onChange={(event) => handleValueChange(event.target.value)}
                          placeholder="Zahl eingeben"
                        />
                      );
                    } else if (field.dataType === 'date') {
                      control = (
                        <input
                          id={labelId}
                          type="date"
                          value={normalizedValue}
                          onChange={(event) => handleValueChange(event.target.value)}
                        />
                      );
                    } else if (field.dataType === 'boolean') {
                      control = (
                        <select
                          id={labelId}
                          value={normalizedValue}
                          onChange={(event) => handleValueChange(event.target.value)}
                        >
                          <option value="">Bitte auswählen</option>
                          <option value="true">Ja</option>
                          <option value="false">Nein</option>
                        </select>
                      );
                    } else if (field.dataType === 'text') {
                      control = (
                        <textarea
                          id={labelId}
                          value={normalizedValue}
                          onChange={(event) => handleValueChange(event.target.value)}
                          rows={field.name?.toLowerCase().includes('kommentar') ? 4 : 3}
                          placeholder="Wert eingeben"
                        />
                      );
                    } else {
                      control = (
                        <input
                          id={labelId}
                          type="text"
                          value={normalizedValue}
                          onChange={(event) => handleValueChange(event.target.value)}
                          placeholder="Wert eingeben"
                        />
                      );
                    }

                    return (
                      <div className="order-device-details__field" key={`${fieldKey}-${index}`}>
                        <label htmlFor={labelId} className="order-device-details__field-label">
                          {field.name}
                          {requiredBadge}
                        </label>
                        {control}
                      </div>
                    );
                  })}
                </form>

                {saveState.successMessage && (
                  <div className="order-device-details__alert order-device-details__alert--success" role="status">
                    {saveState.successMessage}
                  </div>
                )}

                {saveState.errorMessage && (
                  <div className="order-device-details__alert order-device-details__alert--error" role="alert">
                    {saveState.errorMessage}
                  </div>
                )}

                <div className="order-device-details__actions">
                  <button
                    type="button"
                    className="order-device-details__btn order-device-details__btn--secondary"
                    onClick={handleSave}
                    disabled={saveState.isSaving || saveState.isCompleting}
                  >
                    {saveState.isSaving ? 'Speichern...' : 'Speichern'}
                  </button>
                  <button
                    type="button"
                    className="order-device-details__btn order-device-details__btn--primary"
                    onClick={handleComplete}
                    disabled={!allRequiredFieldsFilled || saveState.isSaving || saveState.isCompleting}
                    title={!allRequiredFieldsFilled ? 'Bitte alle Pflichtfelder ausfüllen' : undefined}
                  >
                    {saveState.isCompleting ? 'Abschließen...' : 'Speichern und abschließen'}
                  </button>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>

    <div
      role="tabpanel"
      id="tabpanel-details"
      aria-labelledby="tab-details"
      className="order-device-details__tabpanel"
      hidden={activeTab !== 'details'}
    >
      <div className="order-device-details__panel">
        <section className="order-device-details__section">
          <h4 className="order-device-details__section-title">Geräteinformationen</h4>
          <dl className="order-device-details__definition-list">
            <div className="order-device-details__definition-item">
              <dt>Gerätetyp</dt>
              <dd>{equipmentTypeLabel}</dd>
            </div>
            {metadataEntries.length === 0 ? (
              <div className="order-device-details__empty">
                <span>Keine Metadaten vorhanden</span>
              </div>
            ) : (
              metadataEntries.map((entry, index) => (
                <div className="order-device-details__definition-item" key={`${entry.label}-${index}`}>
                  <dt>{entry.label}</dt>
                  <dd>{valueOrPlaceholder(entry.value)}</dd>
                </div>
              ))
            )}
          </dl>
        </section>

        <section className="order-device-details__section">
          <h4 className="order-device-details__section-title">Vordefinierte Felder</h4>
          {fieldEntries.length === 0 ? (
            <div className="order-device-details__empty">
              <span>Keine Feldwerte vorhanden</span>
            </div>
          ) : (
            <dl className="order-device-details__definition-list">
              {fieldEntries.map((entry, index) => (
                <div className="order-device-details__definition-item" key={`${entry.name}-${index}`}>
                  <dt>{entry.name}</dt>
                  <dd>{combineValueWithUnit(entry.value, entry.unit)}</dd>
                </div>
              ))}
            </dl>
          )}
        </section>

        {device.id && (
          <section className="order-device-details__section">
            <h4 className="order-device-details__section-title">NFC-Tag</h4>
            <NfcPairAction
              orderId={orderId}
              deviceId={device.id}
              currentNfcTagId={currentNfcTagId}
              onPairingSuccess={handleNfcPairingSuccess}
            />
          </section>
        )}
      </div>
    </div>
  </section>
);
};
